import { useState, useContext, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import AsyncSelect from "react-select/async";
import Select from "react-select";
import Header from "../layout/Header";
import Footer from "../layout/Footer";
import Alert from "../Alert";
import PermissionGate from "../layout/PermissionGate";
import { alertContext } from "../contexts/AlertProvider";
import normalize from "../utils/NormalizeSelect";

const PAYMENT_OPTIONS = [
    { value: "Cash on Delivery", label: "Cash on Delivery" },
    { value: "Paid", label: "Paid" },
];

let nextRowId = 1;
const makeEmptyRow = () => ({
    id: nextRowId++,
    merchant: null,
    branch: null,
    driver: null,
    area: null,
    block: null,
    street: null,
    orderDate: "",
    orderTime: "",
    customerName: "",
    customerPhone: "",
    paymentType: null,
    orderAmount: "",
    building: "",
    floor: "",
    apartment: "",
    notes: "",
    referenceId: "",
    branchOptions: [],
    blockOptions: [],
    streetOptions: [],
});

const selectStyles = "react-select-styled react-select-bordered";

const ManualOrderEntry = () => {
    const [rows, setRows] = useState([makeEmptyRow()]);
    const [merchantOptions, setMerchantOptions] = useState([]);
    const [areaOptions, setAreaOptions] = useState([]);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [preview, setPreview] = useState(null);
    const [commitResult, setCommitResult] = useState(null);
    const [alertConfig, setAlertConfig] = useContext(alertContext);
    const token = localStorage.getItem("token");
    const branchCacheRef = useRef({});
    const blockCacheRef = useRef({});
    const streetCacheRef = useRef({});

    const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

    useEffect(() => {
        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}merchant-list`, authHeaders)
            .then((res) => {
                if (res.data.code === 1) {
                    setMerchantOptions(
                        res.data.data.map((m) => ({ value: m._id, label: m.merchantName })),
                    );
                }
            })
            .catch(console.error);

        axios
            .get(`${process.env.REACT_APP_API_ENDPOINT}location/areas`, authHeaders)
            .then((res) => {
                if (res.data.code === 1) {
                    setAreaOptions(
                        res.data.data.map((a) => ({ value: a.value, label: a.value })),
                    );
                }
            })
            .catch(console.error);
        // eslint-disable-next-line
    }, []);

    const loadDriverOptions = useCallback(
        (inputValue, callback) => {
            axios
                .get(
                    `${process.env.REACT_APP_API_ENDPOINT}driver-search?search=${encodeURIComponent(
                        inputValue || "",
                    )}`,
                    authHeaders,
                )
                .then((res) => {
                    if (res.data.code === 1) {
                        callback(
                            res.data.data.map((d) => ({
                                value: d._id,
                                label: `${d.name}${d.workPhone ? ` (${d.workPhone})` : ""}`,
                            })),
                        );
                    } else {
                        callback([]);
                    }
                })
                .catch(() => callback([]));
        },
        // eslint-disable-next-line
        [token],
    );

    const updateRow = (id, patch) => {
        setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    };

    const onMerchantChange = async (id, merchant) => {
        updateRow(id, { merchant, branch: null, branchOptions: [] });
        if (!merchant) return;
        let options = branchCacheRef.current[merchant.value];
        if (!options) {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}branch-list/${merchant.value}`,
                    authHeaders,
                );
                options =
                    res.data.code === 1
                        ? res.data.data.map((b) => ({ value: b._id, label: b.name }))
                        : [];
            } catch {
                options = [];
            }
            branchCacheRef.current[merchant.value] = options;
        }
        updateRow(id, { branchOptions: options });
    };

    const onAreaChange = async (id, area) => {
        updateRow(id, { area, block: null, street: null, blockOptions: [], streetOptions: [] });
        if (!area) return;
        let options = blockCacheRef.current[area.value];
        if (!options) {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}location/blocks/${encodeURIComponent(area.value)}`,
                    authHeaders,
                );
                options =
                    res.data.code === 1
                        ? res.data.data.map((b) => ({ value: b.value, label: b.value }))
                        : [];
            } catch {
                options = [];
            }
            blockCacheRef.current[area.value] = options;
        }
        updateRow(id, { blockOptions: options });
    };

    const onBlockChange = async (id, block, areaOverride) => {
        updateRow(id, { block, street: null, streetOptions: [] });
        if (!block) return;
        const row = rows.find((r) => r.id === id);
        const area = areaOverride ?? row?.area;
        if (!area) return;
        const key = `${area.value}|${block.value}`;
        let options = streetCacheRef.current[key];
        if (!options) {
            try {
                const res = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}location/streets/${encodeURIComponent(
                        area.value,
                    )}/${encodeURIComponent(block.value)}`,
                    authHeaders,
                );
                options =
                    res.data.code === 1
                        ? res.data.data.map((s) => ({ value: s.value, label: s.value }))
                        : [];
            } catch {
                options = [];
            }
            streetCacheRef.current[key] = options;
        }
        updateRow(id, { streetOptions: options });
    };

    const addRow = () => setRows((prev) => [...prev, makeEmptyRow()]);
    const removeRow = (id) =>
        setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));

    const buildPayload = () =>
        rows.map((r) => ({
            merchantId: r.merchant?.value,
            branchId: r.branch?.value,
            driverId: r.driver?.value,
            orderDate: r.orderDate,
            orderTime: r.orderTime,
            customerName: r.customerName,
            customerPhone: r.customerPhone,
            paymentType: r.paymentType?.value,
            orderAmount: r.orderAmount,
            area: r.area?.value,
            block: r.block?.value,
            street: r.street?.value,
            building: r.building,
            floor: r.floor,
            apartment: r.apartment,
            notes: r.notes,
            referenceId: r.referenceId,
        }));

    const resultByRowNumber = (result) => {
        const map = {};
        (result?.rows ?? []).forEach((r) => {
            map[r.rowNumber] = r;
        });
        return map;
    };

    const runPreview = () => {
        setIsPreviewing(true);
        setPreview(null);
        setCommitResult(null);
        axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}order/manual-import/preview-rows`,
                { rows: buildPayload() },
                authHeaders,
            )
            .then((res) => {
                setIsPreviewing(false);
                if (res.data.code === 1) {
                    setPreview(resultByRowNumber(res.data.data));
                } else {
                    setAlertConfig({
                        show: true,
                        message: res.data.message || "Preview failed",
                        type: "danger",
                    });
                }
            })
            .catch((err) => {
                setIsPreviewing(false);
                setAlertConfig({
                    show: true,
                    message: err?.response?.data?.message || "Preview failed",
                    type: "danger",
                });
            });
    };

    const runImport = () => {
        setIsImporting(true);
        axios
            .post(
                `${process.env.REACT_APP_API_ENDPOINT}order/manual-import/commit-rows`,
                { rows: buildPayload() },
                authHeaders,
            )
            .then((res) => {
                setIsImporting(false);
                if (res.data.code === 1) {
                    const byRow = resultByRowNumber(res.data.data);
                    setCommitResult(byRow);
                    setAlertConfig({
                        show: true,
                        message: `Imported ${res.data.data.succeeded} of ${res.data.data.total} order(s)`,
                        type: res.data.data.failed > 0 ? "warning" : "success",
                    });
                } else {
                    setAlertConfig({
                        show: true,
                        message: res.data.message || "Import failed",
                        type: "danger",
                    });
                }
            })
            .catch((err) => {
                setIsImporting(false);
                setAlertConfig({
                    show: true,
                    message: err?.response?.data?.message || "Import failed",
                    type: "danger",
                });
            });
    };

    const filterOption = (option, input) =>
        normalize(option.label).includes(normalize(input));

    const resultFor = (row) => {
        const rowNumber = rows.indexOf(row) + 1;
        return commitResult?.[rowNumber] ?? preview?.[rowNumber] ?? null;
    };

    const stats = (() => {
        const source = commitResult ?? preview;
        if (!source) return null;
        const results = Object.values(source);
        const imported = results.filter((r) => r.orderId).length;
        const ready = results.filter((r) => r.ok && !r.orderId).length;
        const needsFixing = results.filter((r) => !r.ok).length;
        return { total: results.length, imported, ready, needsFixing };
    })();

    return (
        <div className="d-flex flex-column flex-root">
            <Alert />
            <div className="page d-flex flex-row flex-column-fluid">
                <div className="wrapper d-flex flex-column flex-row-fluid" id="kt_wrapper">
                    <Header />
                    <div className="app-body">
                        <div className="toolbar py-3 py-lg-6" id="kt_toolbar">
                            <div
                                id="kt_toolbar_container"
                                className=" container-xxl  d-flex flex-stack flex-wrap gap-2"
                            >
                                <div className="page-title d-flex flex-column align-items-start me-3 py-2 py-lg-0 gap-2">
                                    <h1 className="d-flex text-dark fw-bold m-0 fs-3">
                                        Enter Manual Orders
                                    </h1>
                                </div>
                            </div>
                        </div>

                        <div
                            id="kt_content_container"
                            className="d-flex flex-column-fluid align-items-start container-xxl"
                        >
                            <div className="content flex-row-fluid" id="kt_content">
                                <PermissionGate required={`import_manual_orders`}>
                                    <div className="card mb-6">
                                        <div className="p-6">
                                            <p className="text-muted mb-4">
                                                Pick merchant, branch, driver and address live from
                                                your real data — nothing here is free text, so there's
                                                no risk of a typo matching the wrong record. Each row
                                                creates a completed order exactly like the Excel
                                                import: merchant wallet debited, driver credited,
                                                company commission recorded.
                                            </p>

                                            {stats && (
                                                <div className="d-flex flex-wrap gap-2 mb-4">
                                                    <span className="badge badge-light fw-bold fs-8">
                                                        {stats.total} row{stats.total === 1 ? "" : "s"}
                                                    </span>
                                                    {stats.imported > 0 && (
                                                        <span className="badge badge-light-primary fw-bold fs-8">
                                                            {stats.imported} imported
                                                        </span>
                                                    )}
                                                    {stats.ready > 0 && (
                                                        <span className="badge badge-light-success fw-bold fs-8">
                                                            {stats.ready} ready
                                                        </span>
                                                    )}
                                                    {stats.needsFixing > 0 && (
                                                        <span className="badge badge-light-danger fw-bold fs-8">
                                                            {stats.needsFixing} need fixing
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {rows.map((row) => {
                                                const rowResult = resultFor(row);
                                                const rowNumber = rows.indexOf(row) + 1;
                                                const statusBadge = rowResult?.orderId ? (
                                                    <span className="badge badge-light-primary fw-bold fs-9">
                                                        Imported
                                                    </span>
                                                ) : rowResult?.ok ? (
                                                    <span className="badge badge-light-success fw-bold fs-9">
                                                        Ready
                                                    </span>
                                                ) : rowResult ? (
                                                    <span className="badge badge-light-danger fw-bold fs-9">
                                                        Failed
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-light fw-bold fs-9">Pending</span>
                                                );
                                                const borderColor = rowResult?.orderId
                                                    ? "var(--bs-primary)"
                                                    : rowResult?.ok
                                                        ? "var(--bs-success)"
                                                        : rowResult
                                                            ? "var(--bs-danger)"
                                                            : "var(--bs-border-color, #e4e6ef)";
                                                return (
                                                    <div
                                                        key={row.id}
                                                        className="border rounded mb-4 overflow-hidden"
                                                        style={{ borderLeft: `3px solid ${borderColor}` }}
                                                    >
                                                        <div className="d-flex align-items-center justify-content-between px-4 py-2 bg-light-subtle border-bottom">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <span className="fw-bold fs-7">Row {rowNumber}</span>
                                                                {statusBadge}
                                                            </div>
                                                            {rows.length > 1 && (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-icon btn-light-danger"
                                                                    onClick={() => removeRow(row.id)}
                                                                    title="Remove row"
                                                                >
                                                                    <i className="bi bi-x-lg fs-8" />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="row g-3 p-4">
                                                            <div className="col-md-4">
                                                                <label className="form-label">Merchant</label>
                                                                <Select
                                                                    className={selectStyles}
                                                                    classNamePrefix="react-select"
                                                                    options={merchantOptions}
                                                                    value={row.merchant}
                                                                    filterOption={filterOption}
                                                                    onChange={(v) => onMerchantChange(row.id, v)}
                                                                    placeholder="Select merchant"
                                                                />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label">Branch</label>
                                                                <Select
                                                                    className={selectStyles}
                                                                    classNamePrefix="react-select"
                                                                    options={row.branchOptions}
                                                                    value={row.branch}
                                                                    filterOption={filterOption}
                                                                    isDisabled={!row.merchant}
                                                                    onChange={(v) => updateRow(row.id, { branch: v })}
                                                                    placeholder="Select branch"
                                                                />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label">Driver</label>
                                                                <AsyncSelect
                                                                    className={selectStyles}
                                                                    classNamePrefix="react-select"
                                                                    cacheOptions
                                                                    defaultOptions
                                                                    value={row.driver}
                                                                    loadOptions={loadDriverOptions}
                                                                    onChange={(v) => updateRow(row.id, { driver: v })}
                                                                    placeholder="Search driver by name or phone"
                                                                />
                                                            </div>

                                                            <div className="col-md-3">
                                                                <label className="form-label">Order Date</label>
                                                                <input
                                                                    type="date"
                                                                    className="form-control"
                                                                    value={row.orderDate}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { orderDate: e.target.value })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label">Order Time</label>
                                                                <input
                                                                    type="time"
                                                                    className="form-control"
                                                                    value={row.orderTime}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { orderTime: e.target.value })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label">Payment Type</label>
                                                                <Select
                                                                    className={selectStyles}
                                                                    classNamePrefix="react-select"
                                                                    options={PAYMENT_OPTIONS}
                                                                    value={row.paymentType}
                                                                    onChange={(v) => updateRow(row.id, { paymentType: v })}
                                                                    placeholder="Select type"
                                                                />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label">Order Amount (KWD)</label>
                                                                <input
                                                                    type="number"
                                                                    step="0.001"
                                                                    className="form-control"
                                                                    value={row.orderAmount}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { orderAmount: e.target.value })
                                                                    }
                                                                />
                                                            </div>

                                                            <div className="col-md-6">
                                                                <label className="form-label">Customer Name</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={row.customerName}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { customerName: e.target.value })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-md-6">
                                                                <label className="form-label">Customer Phone</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={row.customerPhone}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { customerPhone: e.target.value })
                                                                    }
                                                                />
                                                            </div>

                                                            <div className="col-md-4">
                                                                <label className="form-label">Area</label>
                                                                <Select
                                                                    className={selectStyles}
                                                                    classNamePrefix="react-select"
                                                                    options={areaOptions}
                                                                    value={row.area}
                                                                    filterOption={filterOption}
                                                                    onChange={(v) => onAreaChange(row.id, v)}
                                                                    placeholder="Select area"
                                                                />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label">Block</label>
                                                                <Select
                                                                    className={selectStyles}
                                                                    classNamePrefix="react-select"
                                                                    options={row.blockOptions}
                                                                    value={row.block}
                                                                    filterOption={filterOption}
                                                                    isDisabled={!row.area}
                                                                    onChange={(v) => onBlockChange(row.id, v)}
                                                                    placeholder="Select block"
                                                                />
                                                            </div>
                                                            <div className="col-md-4">
                                                                <label className="form-label">Street</label>
                                                                <Select
                                                                    className={selectStyles}
                                                                    classNamePrefix="react-select"
                                                                    options={row.streetOptions}
                                                                    value={row.street}
                                                                    filterOption={filterOption}
                                                                    isDisabled={!row.block}
                                                                    onChange={(v) => updateRow(row.id, { street: v })}
                                                                    placeholder="Select street"
                                                                />
                                                            </div>

                                                            <div className="col-md-3">
                                                                <label className="form-label">Building</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={row.building}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { building: e.target.value })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label">Floor</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={row.floor}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { floor: e.target.value })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label">Apartment</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={row.apartment}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { apartment: e.target.value })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-md-3">
                                                                <label className="form-label">Reference Id</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={row.referenceId}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { referenceId: e.target.value })
                                                                    }
                                                                />
                                                            </div>
                                                            <div className="col-md-12">
                                                                <label className="form-label">Notes</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    value={row.notes}
                                                                    onChange={(e) =>
                                                                        updateRow(row.id, { notes: e.target.value })
                                                                    }
                                                                />
                                                            </div>
                                                        </div>

                                                        {rowResult && (
                                                            <div
                                                                className={`d-flex align-items-start gap-2 m-4 mt-0 p-3 rounded ${rowResult.ok
                                                                    ? "bg-light-success text-success"
                                                                    : "bg-light-danger text-danger"
                                                                    }`}
                                                            >
                                                                <i
                                                                    className={`bi ${rowResult.ok
                                                                        ? "bi-check-circle-fill"
                                                                        : "bi-exclamation-triangle-fill"
                                                                        } fs-5 mt-1`}
                                                                />
                                                                <div className="fs-7">
                                                                    {rowResult.ok ? (
                                                                        rowResult.orderId ? (
                                                                            <span>
                                                                                Order <strong>{rowResult.orderId}</strong> created.
                                                                            </span>
                                                                        ) : (
                                                                            <span>
                                                                                Ready — fee {rowResult.summary?.fee} KWD, driver{" "}
                                                                                {rowResult.summary?.driverName}.
                                                                            </span>
                                                                        )
                                                                    ) : (
                                                                        <ul className="mb-0 ps-3">
                                                                            {rowResult.errors?.map((e, i) => (
                                                                                <li key={i}>{e}</li>
                                                                            ))}
                                                                        </ul>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}

                                            <div className="d-flex gap-3">
                                                <button
                                                    type="button"
                                                    className="btn btn-light d-flex align-items-center gap-2"
                                                    onClick={addRow}
                                                >
                                                    <i className="bi bi-plus-lg" /> Add Row
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary d-flex align-items-center gap-2"
                                                    disabled={isPreviewing}
                                                    onClick={runPreview}
                                                >
                                                    <i className="bi bi-eye" />
                                                    {isPreviewing ? "Checking..." : "Preview"}
                                                </button>
                                                {preview && (
                                                    <button
                                                        type="button"
                                                        className="btn btn-success d-flex align-items-center gap-2"
                                                        disabled={isImporting}
                                                        onClick={runImport}
                                                    >
                                                        <i className="bi bi-check2-circle" />
                                                        {isImporting ? "Importing..." : "Confirm Import"}
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </PermissionGate>
                            </div>
                        </div>
                    </div>
                    <Footer />
                </div>
            </div>
        </div>
    );
};

export default ManualOrderEntry;